<template>
  <BaseModal
    title="Join the newsletter"
    :show="uiStore.showNewsletterSignupModal"
    @dismiss="uiStore.showNewsletterSignupModal = false"
  >
    <div v-if="showSuccessState" class="bg-mint space-y-4 rounded-2xl p-7">
      <div>
        <p>
          Keep an eye on your inbox for the latest news, stories and updates.
        </p>
      </div>
      <p class="type-body-sm text-black/50">
        Have a school-aged child? Say you’ll wait on a smartphone and
        <PledgeLink
          location="newsletter_modal"
          class="underline hover:no-underline"
          >join the movement</PledgeLink
        >.
      </p>
    </div>
    <form v-else class="mt-5 space-y-10" @submit.prevent="onSubmit">
      <InputSelect label="I am a" name="role" :options="roleOptions" required />
      <div>
        <InputLabel>My details</InputLabel>
        <div class="grid grid-cols-2 gap-2.5">
          <InputText name="firstName" placeholder="First Name" required />
          <InputText name="lastName" placeholder="Last Name" required />
          <InputText
            class="col-span-2"
            name="email"
            type="email"
            placeholder="Email address"
            required
          />
        </div>
      </div>
      <div>
        <BaseAsyncButton
          :loading="loading"
          type="submit"
          class="btn-md btn-yellow w-full"
        >
          Sign up
        </BaseAsyncButton>
        <InputError :error="errorMessage" />
      </div>
    </form>
  </BaseModal>
</template>

<script setup lang="ts">
import { z } from 'zod'

import { CreateNewsletterSignupDocument } from '#graphql-exports'

const uiStore = useUIStore()

const roleOptions = [
  'Parent or caregiver',
  'Grandparent',
  'School principal',
  'Teacher',
  'Health professional',
  'Advocate',
]

interface CreateNewsletterSignupResponse {
  createNewsletterSignup: {
    id: string
  }
}

interface CreateNewsletterSignupVariables {
  data: {
    firstName: string
    lastName: string
    email: string
    role: string
  }
}

const validationSchema = toTypedSchema(
  z.object({
    role: z.string(),
    firstName: z.string().min(1, 'Required'),
    lastName: z.string().min(1, 'Required'),
    email: z.email(),
  }),
)

const { handleSubmit } = useForm({
  initialValues: {
    role: 'Parent or caregiver',
    email: '',
    firstName: '',
    lastName: '',
  },
  validationSchema,
})

const errorMessage = ref('')
const showSuccessState = ref(false)

const { mutate, loading } = useMutation<
  CreateNewsletterSignupResponse,
  CreateNewsletterSignupVariables
>(CreateNewsletterSignupDocument)

const onSubmit = handleSubmit(async (values) => {
  errorMessage.value = ''

  await mutate({
    data: {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      role: values.role,
    },
  })
    .then(() => {
      showSuccessState.value = true
    })
    .catch(
      (res: PayloadGraphQLErrorResponse<CreateNewsletterSignupResponse>) => {
        if (
          res.errors[0]?.extensions.name === 'ValidationError' &&
          res.errors[0]?.extensions.data.errors[0]?.path === 'email'
        ) {
          errorMessage.value = 'You have already signed up for the newsletter.'
        } else {
          errorMessage.value =
            res.errors[0]?.message ?? 'An error occurred, please try again.'
        }
      },
    )
})
</script>
